'use client';

import { useEffect, useState } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { usePathname } from 'next/navigation';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Menu, Package, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { cn } from '@kit/ui/utils';

import { OiaHeaderAccountSection } from './header-account-section';

const navLinks = [
  { labelKey: 'landing:nav.ourStory', fallback: 'OUR STORY', href: '/#our-story' },
  { labelKey: 'landing:nav.membership', fallback: 'MEMBERSHIP', href: '/#membership' },
  { labelKey: 'landing:nav.facilities', fallback: 'FACILITIES', href: '/#facilities' },
  { labelKey: 'landing:nav.faq', fallback: 'FAQ', href: '/#faqs' },
  { labelKey: 'landing:nav.contact', fallback: 'CONTACT', href: '/#contact' },
];

const authNavLinks = [
  {
    labelKey: 'landing:nav.myBookings',
    fallback: 'MY BOOKINGS',
    href: '/bookings',
    icon: Calendar,
  },
  {
    labelKey: 'landing:nav.myPackages',
    fallback: 'MY PACKAGES',
    href: '/packages',
    icon: Package,
  },
];

const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/faq',
  '/event',
  '/coaches',
  '/membership',
  '/privacy-policy',
  '/terms-of-service',
];

export function OiaHeader() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useSession();
  const isAuthenticated = !!session.data;

  const isLandingPage = pathname === '/';
  const shouldBeSolid = solidNavbarRoutes.some((route) =>
    pathname?.startsWith(route),
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const isOpaque = mobileMenuOpen || shouldBeSolid || scrolled;

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          isOpaque ? 'bg-[var(--oia-taupe)] py-3' : 'bg-transparent py-5',
        )}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link href="/" className="relative z-10 flex-shrink-0">
            <Image
              src="/landing-pages/oia/images/logo.png"
              alt="OIA Downtown"
              width={140}
              height={56}
              className={cn(
                'h-10 w-auto transition-[filter] duration-300 lg:h-14',
                !isOpaque && 'brightness-0 invert',
              )}
              priority
            />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex xl:gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  'font-oia-eyebrow whitespace-nowrap text-[11px] transition-colors duration-300',
                  isOpaque
                    ? 'text-[var(--oia-ink)]/75 hover:text-[var(--oia-ink)]'
                    : 'text-white/85 hover:text-white',
                )}
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated &&
              authNavLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                  'font-oia-eyebrow whitespace-nowrap text-[11px] transition-colors duration-300',
                  isOpaque
                    ? 'text-[var(--oia-ink)]/75 hover:text-[var(--oia-ink)]'
                    : 'text-white/85 hover:text-white',
                )}
                >
                  {t(link.labelKey, { defaultValue: link.fallback })}
                </Link>
              ))}
          </nav>

          <div className="hidden items-center lg:flex">
            <OiaHeaderAccountSection isOpaque={isOpaque} />
          </div>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={cn(
              '-mr-2 p-2 lg:hidden',
              isOpaque ? 'text-[var(--oia-ink)]' : 'text-white',
            )}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <div
            className="fixed inset-0 top-[64px] z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        <div
          className={cn(
            'absolute top-full right-0 left-0 z-50 overflow-hidden bg-[var(--oia-taupe)] transition-all duration-300 lg:hidden',
            mobileMenuOpen ? 'max-h-[600px]' : 'max-h-0',
          )}
        >
          <nav className="flex flex-col px-6 pb-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="font-oia-eyebrow border-b border-[var(--oia-line)] py-3 text-[12px] text-[var(--oia-ink)]/80 transition-colors duration-300 hover:text-[var(--oia-ink)]"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated && (
              <>
                {authNavLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="font-oia-eyebrow flex items-center gap-3 border-b border-[var(--oia-line)] py-3 text-[12px] text-[var(--oia-ink)]/80 transition-colors duration-300 hover:text-[var(--oia-ink)]"
                    >
                      <Icon className="h-5 w-5" />
                      {t(link.labelKey, { defaultValue: link.fallback })}
                    </Link>
                  );
                })}
              </>
            )}
            <div className="pt-4">
              <OiaHeaderAccountSection
                onNavigate={() => setMobileMenuOpen(false)}
              />
            </div>
          </nav>
        </div>
      </header>
      {!isLandingPage && <div className="h-[72px]" />}
    </>
  );
}

function useSession() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: listener } = client.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
    });

    return () => listener.subscription.unsubscribe();
  }, [client.auth, queryClient]);

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}
